import { useContext, useMemo } from 'react'

import {
  type FrameBuilderArtworkImage,
  type FrameBuilderExtraService,
  type FrameBuilderGlass,
  type FrameBuilderPassepartoutColor,
  type FrameBuilderPassepartoutSize,
  type FrameBuilderProfile,
  type FrameBuilderSpacerColor,
  type FrameBuilderSpacerSize,
  type FrameBuilderTexture,
} from './state'
import {
  FrameBuilderCommittedStateContext,
  FrameBuilderSelectedStateContext,
} from '../contexts/state-context'

export interface SelectionMessages {
  artworkSize?: string
  artworkImage?: string
  frameStyle?: string
  passepartout?: string
  glass?: string
  extras?: string
}

/**
 * Gets artwork size selection.
 */
const getArtworkSizeSelection = (
  artworkWidth?: number,
  artworkHeight?: number,
): SelectionMessages => {
  if (
    typeof artworkWidth === 'undefined' ||
    typeof artworkHeight === 'undefined'
  ) {
    return {
      artworkSize: undefined,
    }
  }

  return {
    artworkSize: `${artworkWidth} x ${artworkHeight} cm`,
  }
}

/**
 * Gets artwork image selection.
 */
const getArtworkImageSelection = (
  artworkImage?: FrameBuilderArtworkImage,
): SelectionMessages => {
  if (typeof artworkImage === 'undefined') {
    return {
      artworkImage: undefined,
    }
  }

  return {
    artworkImage: artworkImage.name,
  }
}

/**
 * Gets frame style selection.
 */
const getFrameStyleSelection = (
  profile?: FrameBuilderProfile,
  texture?: FrameBuilderTexture,
): SelectionMessages => {
  if (typeof profile === 'undefined' || typeof texture === 'undefined') {
    return {
      frameStyle: undefined,
    }
  }

  return {
    frameStyle: `${texture.title} | ${profile.title}`,
  }
}

/**
 * Gets passepartout selection.
 */
const getPassepartoutSelection = (
  passepartoutSize?: FrameBuilderPassepartoutSize,
  passepartoutColor?: FrameBuilderPassepartoutColor,
): SelectionMessages => {
  if (typeof passepartoutSize === 'undefined') {
    return {
      passepartout: undefined,
    }
  }

  const colorTitle =
    typeof passepartoutColor !== 'undefined' && passepartoutSize.size > 0
      ? passepartoutColor.title
      : undefined

  return {
    passepartout: [passepartoutSize.title, colorTitle]
      .filter(Boolean)
      .join(' | '),
  }
}

/**
 * Gets glass selection.
 */
const getGlassSelection = (glass?: FrameBuilderGlass): SelectionMessages => {
  if (typeof glass === 'undefined') {
    return {
      glass: undefined,
    }
  }

  return {
    glass: glass.title,
  }
}

/**
 * Gets extras selection.
 */
const getExtrasSelection = (
  extraServices?: FrameBuilderExtraService[],
  spacerSize?: FrameBuilderSpacerSize,
  spacerColor?: FrameBuilderSpacerColor,
): SelectionMessages => {
  if (
    typeof extraServices === 'undefined' ||
    extraServices.length === 0 ||
    typeof spacerSize === 'undefined' ||
    typeof spacerColor === 'undefined'
  ) {
    return {
      extras: undefined,
    }
  }

  return {
    extras: [
      ...extraServices.map((extraService) => extraService.title),
      spacerSize.title,
      spacerColor.title,
    ]
      .filter(Boolean)
      .join(' | '),
  }
}

/**
 * Manages frame builder selection messages.
 */
export const useSelectionMessages = () => {
  const { committedState } = useContext(FrameBuilderCommittedStateContext)
  const { selectedState } = useContext(FrameBuilderSelectedStateContext)

  const committedStateSelectionMessages = useMemo<SelectionMessages>(
    () => ({
      ...getArtworkSizeSelection(
        committedState.artwork.width,
        committedState.artwork.height,
      ),
      ...getArtworkImageSelection(committedState.artwork.image),
      ...getFrameStyleSelection(committedState.profile, committedState.texture),
      ...getPassepartoutSelection(
        committedState.passepartout.size,
        committedState.passepartout.color,
      ),
      ...getGlassSelection(committedState.glass),
      ...getExtrasSelection(
        committedState.extras.extraServices,
        committedState.extras.spacer.size,
        committedState.extras.spacer.color,
      ),
    }),
    [
      committedState.artwork.width,
      committedState.artwork.height,
      committedState.artwork.image,
      committedState.profile,
      committedState.texture,
      committedState.passepartout.size,
      committedState.passepartout.color,
      committedState.glass,
      committedState.extras.extraServices,
      committedState.extras.spacer.size,
      committedState.extras.spacer.color,
    ],
  )

  const selectedStateSelectionMessages = useMemo<SelectionMessages>(
    () => ({
      ...getArtworkSizeSelection(
        selectedState.artwork.width,
        selectedState.artwork.height,
      ),
      ...getArtworkImageSelection(selectedState.artwork.image),
    }),
    [
      selectedState.artwork.width,
      selectedState.artwork.height,
      selectedState.artwork.image,
    ],
  )

  return [
    committedStateSelectionMessages,
    selectedStateSelectionMessages,
  ] as const
}
